const DISCOUNT_FAMILIES = [
  {
    key: 'coupon_discount',
    label: 'Coupon Discount',
    aliases: [
      'guest_coupon_discount',
      'coupon_discount',
      'couponDiscount',
      'discount',
      'finance_discount',
      'finance_coupon_discount',
    ],
  },
  {
    key: 'weekly_discount',
    label: 'Weekly Discount',
    aliases: [
      'guest_weekly_discount',
      'weekly_discount',
      'weeklyDiscount',
      'finance_weekly_discount',
    ],
  },
  {
    key: 'monthly_discount',
    label: 'Monthly Discount',
    aliases: [
      'guest_monthly_discount',
      'monthly_discount',
      'monthlyDiscount',
      'finance_monthly_discount',
    ],
  },
  {
    key: 'last_minute_discount',
    label: 'Last Minute Discount',
    aliases: [
      'guest_last_minute_discount',
      'last_minute_discount',
      'lastMinuteDiscount',
      'finance_last_minute_discount',
    ],
  },
  {
    key: 'employee_discount',
    label: 'Employee Discount',
    aliases: [
      'guest_employee_discount',
      'employee_discount',
      'employeeDiscount',
      'finance_employee_discount',
    ],
  },
  {
    key: 'shareholder_discount',
    label: 'Shareholder Discount',
    aliases: [
      'guest_shareholder_discount',
      'shareholder_discount',
      'shareholderDiscount',
      'finance_shareholder_discount',
    ],
  },
  {
    key: 'other_special_discount',
    label: 'Other Special Discount',
    aliases: [
      'guest_other_special_discount',
      'other_special_discount',
      'otherSpecialDiscount',
      'finance_other_special_discount',
    ],
  },
];

function currencyNumber(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : null;
}

function objectFromJson(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeContractItems(items) {
  const seen = new Set();
  const normalized = [];

  for (const [index, item] of items.entries()) {
    if (!item || typeof item !== 'object') continue;
    const amount = currencyNumber(item.amount);
    if (amount == null || Math.abs(amount) < 0.005) continue;
    const key = String(item.key || `discount_${index}`).trim() || `discount_${index}`;
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push({
      key,
      label: String(item.label || 'Discount').trim() || 'Discount',
      amount,
    });
  }

  return normalized;
}

/**
 * Derive display-only discount rows from fields already present in a fresh
 * financial row. Aliases in one family are alternatives, so the same discount
 * cannot be counted twice when both normalized and raw keys are present.
 */
export function deriveDiscountLineItemsFromRawFinance(rawFinance, fresh = {}) {
  const source = {
    ...objectFromJson(rawFinance),
    ...(fresh && typeof fresh === 'object' ? fresh : {}),
  };

  return DISCOUNT_FAMILIES.flatMap(({ key, label, aliases }) => {
    for (const alias of aliases) {
      const amount = currencyNumber(source[alias]);
      if (amount != null && Math.abs(amount) >= 0.005) {
        return [{ key, label, amount }];
      }
    }
    return [];
  });
}

/**
 * Resolve the rows a financial folio should display. The derived backend
 * contract wins whenever it exists, even when it is an empty array. Raw fresh
 * fields support the admin folio until that endpoint exposes the contract;
 * coupon/weekly inputs are retained only for older owner responses.
 */
export function resolveFinancialDiscountLineItems({
  discountLineItems,
  discountsTotal,
  rawFinance,
  fresh,
  legacyCouponDiscount,
  legacyWeeklyDiscount,
} = {}) {
  const hasLineItemContract = Array.isArray(discountLineItems);
  const contractTotal = currencyNumber(discountsTotal);

  if (hasLineItemContract) {
    const items = normalizeContractItems(discountLineItems);
    if (items.length > 0) return items;
    if (contractTotal != null && Math.abs(contractTotal) >= 0.005) {
      return [{ key: 'discounts_total', label: 'Discounts', amount: contractTotal }];
    }
    return [];
  }

  if (contractTotal != null) {
    return Math.abs(contractTotal) >= 0.005
      ? [{ key: 'discounts_total', label: 'Discounts', amount: contractTotal }]
      : [];
  }

  if (rawFinance != null || fresh != null) {
    const derived = deriveDiscountLineItemsFromRawFinance(rawFinance, fresh);
    if (derived.length > 0) return derived;
  }

  return normalizeContractItems([
    { key: 'coupon_discount', label: 'Coupon Discount', amount: legacyCouponDiscount },
    { key: 'weekly_discount', label: 'Weekly Discount', amount: legacyWeeklyDiscount },
  ]);
}

export function sumFinancialDiscountLineItems(items) {
  return Math.round((items || []).reduce((total, item) => {
    const amount = currencyNumber(item?.amount);
    return total + (amount ?? 0);
  }, 0) * 100) / 100;
}

/**
 * Detect the narrow legacy pattern where a stored owner payout exactly matches
 * its displayed components before discounts. This is audit evidence only; the
 * caller must not use the result to replace the canonical stored total.
 */
export function findStoredOwnerPayoutDiscountMismatch({
  storedOwnerPayout,
  undiscountedBreakdownTotal,
  discountLineItems,
} = {}) {
  const stored = currencyNumber(storedOwnerPayout);
  const undiscounted = currencyNumber(undiscountedBreakdownTotal);
  if (stored == null || undiscounted == null || Math.abs(stored - undiscounted) > 0.01) return null;

  const discountsTotal = sumFinancialDiscountLineItems(discountLineItems);
  if (Math.abs(discountsTotal) < 0.005) return null;

  const displayedBreakdownTotal = Math.round((undiscounted + discountsTotal) * 100) / 100;
  if (Math.abs(stored - displayedBreakdownTotal) <= 0.01) return null;

  return {
    storedOwnerPayout: stored,
    displayedBreakdownTotal,
    discountsTotal,
    difference: Math.round((stored - displayedBreakdownTotal) * 100) / 100,
  };
}
